import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type { Page } from '@clawhire/shared';
import { api, type QueryParams } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import type { FilterValues } from '@/components/data/filters';
import type {
  MessageTemplateRow,
  RenderedMessage,
  SubmissionDetail,
  SubmissionListItem,
  SubmissionPatch,
} from '@/pages/submissions/types';

export const SUBMISSIONS_ENTITY = 'submissions';
/** The list is sorted/paged client side, so ask for the API maximum in one go. */
export const LIST_PAGE_SIZE = 200;

/** FilterBar values -> `GET /submissions` query params. `waitingOn` is single valued on the API. */
export function listQueryParams(filters: FilterValues): QueryParams {
  const params: QueryParams = { ...filters, pageSize: LIST_PAGE_SIZE };
  const waitingOn = filters.waitingOn;
  if (Array.isArray(waitingOn)) params.waitingOn = waitingOn[0];
  return params;
}

/** The previous page stays visible while a changed filter (e.g. debounced text) loads. */
export function useSubmissionList(filters: FilterValues) {
  return useQuery({
    queryKey: queryKeys.list(SUBMISSIONS_ENTITY, filters),
    queryFn: () => api.get<Page<SubmissionListItem>>('/submissions', { query: listQueryParams(filters) }),
    placeholderData: keepPreviousData,
  });
}

export function useSubmission(ref: string) {
  return useQuery({
    queryKey: queryKeys.detail(SUBMISSIONS_ENTITY, ref),
    queryFn: () => api.get<SubmissionDetail>(`/submissions/${encodeURIComponent(ref)}`),
    enabled: ref.length > 0,
  });
}

/** After any write: lists of submissions, positions and candidates (counts, stages) and the dashboard. */
export async function invalidateAfterSubmissionChange(queryClient: QueryClient, submissionId?: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [SUBMISSIONS_ENTITY, 'list'] }),
    queryClient.invalidateQueries({ queryKey: ['positions'] }),
    queryClient.invalidateQueries({ queryKey: ['candidates'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard }),
    ...(submissionId
      ? [queryClient.invalidateQueries({ queryKey: queryKeys.activity('SUBMISSION', submissionId) })]
      : []),
  ]);
}

function useDetailMutation<TVariables>(
  ref: string,
  mutationFn: (variables: TVariables) => Promise<SubmissionDetail>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async (detail) => {
      queryClient.setQueryData(queryKeys.detail(SUBMISSIONS_ENTITY, ref), detail);
      await invalidateAfterSubmissionChange(queryClient, detail.id);
    },
  });
}

/** `PATCH /submissions/:id` — rates, tracking, notes. Errors propagate to the caller. */
export function useUpdateSubmission(ref: string) {
  return useDetailMutation<SubmissionPatch>(ref, (patch) =>
    api.patch<SubmissionDetail>(`/submissions/${encodeURIComponent(ref)}`, patch),
  );
}

/** `POST /submissions/:id/margin` — `marginPct: null` clears the override ("use position/global"). */
export function useSetSubmissionMargin(ref: string) {
  return useDetailMutation<{ marginPct: number | null; reason?: string | null }>(ref, (body) =>
    api.post<SubmissionDetail>(`/submissions/${encodeURIComponent(ref)}/margin`, body),
  );
}

/** `POST /submissions/:id/recalculate-offer` — Screening only (409 OFFER_FIXED otherwise). */
export function useRecalculateOffer(ref: string) {
  return useDetailMutation<void>(ref, () =>
    api.post<SubmissionDetail>(`/submissions/${encodeURIComponent(ref)}/recalculate-offer`),
  );
}

/** `DELETE /submissions/:id` — soft delete. The detail cache is dropped so a stale page cannot come back. */
export function useDeleteSubmission(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/submissions/${encodeURIComponent(ref)}`),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: queryKeys.detail(SUBMISSIONS_ENTITY, ref) });
      await invalidateAfterSubmissionChange(queryClient);
    },
  });
}

export function useMessageTemplates(enabled: boolean) {
  return useQuery({
    queryKey: ['message-templates'],
    queryFn: async () => {
      const data = await api.get<unknown>('/message-templates');
      if (Array.isArray(data)) return data as MessageTemplateRow[];
      const items = (data as { items?: unknown } | null)?.items;
      return Array.isArray(items) ? (items as MessageTemplateRow[]) : [];
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** `POST /messages/render` is read-only (nothing is sent), so it is modelled as a query. */
export function useRenderedMessage(templateKey: string, submissionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['messages', 'render', templateKey, submissionId],
    queryFn: () => api.post<RenderedMessage>('/messages/render', { templateKey, submission: submissionId }),
    enabled: enabled && templateKey.length > 0,
    staleTime: 0,
    gcTime: 0,
  });
}
